import React, { useState, useEffect, Suspense } from "react";
import * as Sentry from "@sentry/react";
import Layout from "./components/Layout";
import { Toaster } from "./components/ui/sonner";
import { Backdrop } from "./components/ui/backdrop";
import { Spinner } from "./components/ui/spinner";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useClarity } from "./hooks/useClarity";
import { phCapture, PH } from "./libs/posthog";
import { ErrorBoundary } from "./components/errors/ErrorBoundary";
import { VersionUpdateBanner } from "./components/VersionUpdateBanner";

// Route-level lazy loading — each page becomes its own async chunk
const StartConsultation = React.lazy(() => import("./pages/StartConsultation"));
const WaitingRoom = React.lazy(() => import("./pages/WaitingRoom"));
const DoctorWaitingRoom = React.lazy(
  () => import("./pages/doctor/DoctorWaitingRoom"),
);
const VideoAudioApp = React.lazy(() => import("./pages/VideoAudioApp"));
const Chat = React.lazy(() => import("./pages/Chat"));
const DoctorChat = React.lazy(() => import("./pages/doctor/DoctorChat"));
const DoctorVideoAudioConsultation = React.lazy(
  () => import("./pages/doctor/DoctorVideoAudioConsultation"),
);
const ConcludeDoctorCommunication = React.lazy(
  () => import("./pages/doctor/ConcludeDoctorCommunication"),
);
const ConcludeDoctorConsultation = React.lazy(
  () => import("./pages/doctor/ConcludeDoctorConsultation"),
);
const ReviewDoctorRecommendation = React.lazy(
  () => import("./pages/doctor/ReviewDoctorRecommendation"),
);
const RateConsultation = React.lazy(() => import("./pages/RateConsultation"));
const AfterConsultation = React.lazy(() => import("./pages/AfterConsultation"));
const Success = React.lazy(() => import("./pages/Success"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

import { PageLoader } from "./components/ui/page-loader";

const router = createBrowserRouter([
  {
    path: "/",
    element: <StartConsultation />,
  },
  {
    path: "/:consultationId",
    element: (
      <ErrorBoundary>
        <WaitingRoom />
      </ErrorBoundary>
    ),
  },
  {
    path: "/doctor/:consultationId",
    element: (
      <ErrorBoundary>
        <DoctorWaitingRoom />
      </ErrorBoundary>
    ),
  },
  {
    path: "/voice/:consultationId",
    element: (
      <ErrorBoundary>
        <VideoAudioApp />
      </ErrorBoundary>
    ),
  },
  {
    path: "/video/:consultationId",
    element: (
      <ErrorBoundary>
        <VideoAudioApp />
      </ErrorBoundary>
    ),
  },
  {
    path: "/chat/:roomId",
    element: (
      <ErrorBoundary>
        <Chat />
      </ErrorBoundary>
    ),
  },
  {
    path: "/doctor/chat/:roomId",
    element: (
      <ErrorBoundary>
        <DoctorChat />
      </ErrorBoundary>
    ),
  },
  {
    path: "/doctor/video/:consultationId",
    element: (
      <ErrorBoundary>
        <DoctorVideoAudioConsultation />
      </ErrorBoundary>
    ),
  },
  {
    path: "/doctor/voice/:consultationId",
    element: (
      <ErrorBoundary>
        <DoctorVideoAudioConsultation />
      </ErrorBoundary>
    ),
  },
  {
    path: "/doctor/conclude-communication/:consultationId",
    element: (
      <ErrorBoundary>
        <ConcludeDoctorCommunication />
      </ErrorBoundary>
    ),
  },
  {
    path: "/doctor/conclude-consultation/:consultationId",
    element: (
      <ErrorBoundary>
        <ConcludeDoctorConsultation />
      </ErrorBoundary>
    ),
  },
  {
    path: "/doctor/review-recommendations/:consultationId",
    element: (
      <ErrorBoundary>
        <ReviewDoctorRecommendation />
      </ErrorBoundary>
    ),
  },
  {
    path: "/rate-consultation/:consultationId",
    element: (
      <ErrorBoundary>
        <RateConsultation />
      </ErrorBoundary>
    ),
  },
  {
    path: "/after-consultation/:consultationId",
    element: (
      <ErrorBoundary>
        <AfterConsultation />
      </ErrorBoundary>
    ),
  },
  {
    path: "/retry/:consultationId",
    element: (
      <ErrorBoundary>
        <RateConsultation />
      </ErrorBoundary>
    ),
  },
  {
    path: "/success",
    element: <Success />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);

// Fire a $pageview on every navigation. router.subscribe is the correct hook
// point here because useLocation isn't available above <RouterProvider>.
router.subscribe((state) => {
  if (state.navigation.state === "idle") {
    phCapture(PH.$PAGEVIEW, {
      $current_url: window.location.href,
      path: new URL(window.location.href).pathname,
    });
  }
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const AppContext = React.createContext<any>({
  loading: false,
  setLoading: () => {},
  setText: () => {},
  showAwaitingRes: false,
  setShowAwaitingRes: () => {},
});

const queryClient = new QueryClient();

const RouteLoadingFallback = () => <PageLoader />;

function App() {
  useClarity();
  useEffect(() => {
    (window as Window & { _appMounted?: boolean })._appMounted = true;
    document.getElementById("app-loading")?.remove();
    (
      window as Window & { _clearAppLoadTimeout?: () => void }
    )._clearAppLoadTimeout?.();
  }, []);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAwaitingRes, setShowAwaitingRes] = useState(false);

  return (
    <QueryClientProvider client={queryClient}>
      <VersionUpdateBanner />
      <Toaster
        position="bottom-right"
        toastOptions={{
          classNames: {
            toast:
              "bg-surface border border-border shadow-level-1 rounded-lg text-foreground font-sans text-sm",
            description: "text-foreground-muted",
            actionButton: "bg-primary text-primary-foreground",
            cancelButton: "bg-muted text-foreground-muted",
            error: "border-l-4 border-error",
            success: "border-l-4 border-success",
            warning: "border-l-4 border-warning",
            info: "border-l-4 border-info",
          },
        }}
      />
      <AppContext.Provider
        value={{
          loading,
          setLoading,
          setText,
          showAwaitingRes,
          setShowAwaitingRes,
        }}
      >
        <Layout>
          <Suspense fallback={<RouteLoadingFallback />}>
            <RouterProvider router={router} />
          </Suspense>
          <Backdrop open={loading}>
            <div className="flex items-center justify-center gap-6 flex-col">
              {text && (
                <h3 className="text-3xl font-semibold text-primary-foreground">
                  {text}
                </h3>
              )}
              <Spinner size="lg" className="border-white/30 border-t-white" />
            </div>
          </Backdrop>
        </Layout>
      </AppContext.Provider>
    </QueryClientProvider>
  );
}

export default Sentry.withProfiler(App);
